import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { InvoiceDetail } from '@faktorplus/shared';
import { useInvoiceList, useSettleCorrection } from '../../api/invoices';
import { Button } from '../../components/ui/button';
import { Field, Select } from '../../components/ui/form';
import { ErrorBox } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import { formatDate, formatMoney } from '../../lib/format';
import styles from './invoice-detail.module.css';

/**
 * The one action a correcting invoice has: settling its delta (24.09.2026).
 *
 * The card tells the whole story in one place — what it corrects, by how
 * much, and whether that is accounted for. An open correction offers the two
 * agreed ways out: against a later unpaid invoice of the same vendor (its
 * amount due moves, with the correction's number on it), or by hand, when the
 * vendor returned the money outside the system.
 */
export function CorrectionCard({ invoice }: { invoice: InvoiceDetail }) {
  const [targetId, setTargetId] = useState('');
  const settle = useSettleCorrection();
  const isSettled = invoice.correctionSettledAt !== null;

  // Candidate targets: the vendor's live, unpaid transfer invoices.
  const { data: unpaid } = useInvoiceList(
    {
      vendorId: invoice.vendorId,
      onlyUnpaid: true,
      excludeCash: true,
      pageSize: 200,
      sortBy: 'dueDate',
      sortDir: 'asc',
    },
    { enabled: !isSettled },
  );
  const targets = (unpaid?.items ?? []).filter((candidate) => candidate.id !== invoice.id);

  return (
    <div className={`${styles.actionCard} ${isSettled ? '' : styles.actionCardTinted}`}>
      <p className={styles.actionTitle}>Faktura korygująca</p>

      <p className={styles.actionNote}>
        Koryguje fakturę{' '}
        {invoice.correctsInvoiceId ? (
          <Link to={`/faktury/${invoice.correctsInvoiceId}`}>{invoice.correctsNumber}</Link>
        ) : (
          invoice.correctsNumber
        )}{' '}
        o <b>{formatMoney(invoice.grossAmount)}</b> brutto.
      </p>

      {isSettled ? (
        <p className={styles.actionNote}>
          Rozliczona {formatDate(invoice.correctionSettledAt)}
          {invoice.correctionSettledByName && <> przez {invoice.correctionSettledByName}</>}.
        </p>
      ) : (
        <>
          <p className={styles.actionNote}>
            Saldo tej korekty jest w rozrachunkach przy kontrahencie, dopóki jej nie rozliczysz —
            na kolejnej fakturze albo jako zwrot.
          </p>

          <Field
            label="Rozlicz na fakturze"
            hint="Kwota do zapłaty wybranej faktury zmieni się o korektę, z adnotacją."
          >
            {(id) => (
              <Select
                id={id}
                value={targetId}
                onChange={(event) => setTargetId(event.target.value)}
              >
                <option value="">— wybierz niezapłaconą fakturę</option>
                {targets.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.number} · do zapłaty {formatMoney(candidate.remainingAmount)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {settle.error && (
            <ErrorBox>
              {settle.error instanceof ApiError
                ? settle.error.message
                : 'Nie udało się rozliczyć korekty.'}
            </ErrorBox>
          )}

          <Button
            variant="primary"
            disabled={!targetId}
            isLoading={settle.isPending}
            onClick={() =>
              void settle.mutateAsync({ id: invoice.id, input: { targetInvoiceId: targetId } })
            }
          >
            Rozlicz na tej fakturze
          </Button>
          <Button
            isLoading={settle.isPending}
            onClick={() =>
              void settle.mutateAsync({ id: invoice.id, input: { targetInvoiceId: null } })
            }
          >
            Oznacz jako rozliczoną
          </Button>
          <p className={styles.actionNote}>
            Gdy kontrahent zwrócił różnicę albo rozliczyliście ją poza systemem.
          </p>
        </>
      )}
    </div>
  );
}
